// =====================================================
// 📁 POSCustomerField.tsx
// =====================================================

import { useEffect, useRef, useState } from 'react';

import SvgIcon from '../../SvgIcon/SvgIcon';

import CustomerRegistrationPopup from '../../Customer/CustomerRegistrationPopup';

import useDebounced from '../../../hooks/useDebounced';

import InputIconByKey from '../../Forms/Input/InputIconByKey';

interface OptionItem {
  id: number;

  name: string;
}

interface POSCustomerFieldProps {
  label?: string;

  options: OptionItem[];

  selectedValue: number | null;

  onSelect: (selected: OptionItem) => void;

  onReload?: () => void;

  onCustomerCreated?: () => void | Promise<void>;

  openCustomerModal?: () => void;

  closeCustomerModal?: () => void;

  showCustomerModal?: boolean;

  disabled?: boolean;

  error?: string;
}

const POSCustomerField: React.FC<POSCustomerFieldProps> = ({
  label = 'Cliente',

  options,

  selectedValue,

  onSelect,

  onReload,

  showCustomerModal = false,

  openCustomerModal,

  closeCustomerModal,

  onCustomerCreated,

  disabled = false,

  error,
}) => {
  // =====================================================
  // 🔹 State
  // =====================================================

  const [isOpen, setIsOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSearch = useDebounced(searchTerm, 250);

  const [filteredOptions, setFilteredOptions] = useState(options);

  // =====================================================
  // 🔹 Refs
  // =====================================================

  const containerRef = useRef<HTMLDivElement>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // =====================================================
  // 🔹 Selected
  // =====================================================

  const selectedOption =
    options.find((opt) => opt.id === selectedValue) || null;

  // =====================================================
  // 🔹 Search
  // =====================================================

  useEffect(() => {
    const term = debouncedSearch.toLowerCase();

    if (!term) {
      setFilteredOptions(options);

      return;
    }

    setFilteredOptions(
      options.filter((option) => option.name.toLowerCase().includes(term))
    );
  }, [debouncedSearch, options]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  // =====================================================
  // 🔹 Click Outside
  // =====================================================

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <div className="flex flex-col gap-1" ref={containerRef}>
      {/* 🔹 Label */}
      <label
        className="
        text-xs
        font-medium
        text-body
        dark:text-bodydark
      "
      >
        {label}
      </label>

      {/* ================================================= */}
      {/* 🔹 Main Row */}
      {/* ================================================= */}

      <div className="flex min-w-0 items-center gap-2">
        {/* ================================================= */}
        {/* 🔹 Select */}
        {/* ================================================= */}

        <div className="relative min-w-0 flex-1">
          <div
            onClick={() => !disabled && setIsOpen((prev) => !prev)}
            className={`
            flex
            h-[44px]
            cursor-pointer
            items-center

            overflow-hidden

            rounded-xl
            border

            ${
              error
                ? 'border-danger'
                : isOpen
                ? 'border-primary'
                : 'border-stroke dark:border-strokedark'
            }

            bg-white
            shadow-1

            transition-all

            duration-200
            hover:border-primary

            hover:shadow-2
            dark:bg-boxdark-2
          `}
          >
            {/* Icon */}
            <div
              className="
              flex
              h-full
              w-[48px]

              items-center
              justify-center

              border-r
              border-stroke

              bg-gray

              dark:border-strokedark
              dark:bg-meta-4
            "
            >
              <InputIconByKey iconKey="user" />
            </div>

            {/* Value */}
            <div
              className="
              flex-1
              truncate

              px-4

              text-sm
             text-black
             dark:text-bodydark1
            "
            >
              <span
                title={selectedOption?.name}
                className="
                block
                overflow-hidden
                text-ellipsis
                whitespace-nowrap
              "
              >
                {selectedOption?.name || 'Seleccionar cliente'}
              </span>
            </div>

            {/* Chevron */}
            <div
              className="
              flex
              h-full
              w-[44px]

              items-center
              justify-center
            "
            >
              <SvgIcon
                name="chevron-down"
                className={`
                h-5
                w-5

                transition-transform
                duration-200

                ${isOpen ? 'rotate-180 text-primary' : 'text-slate-400'}
              `}
              />
            </div>
          </div>

          {/* ================================================= */}
          {/* 🔹 Dropdown */}
          {/* ================================================= */}

          {isOpen && (
            <div
              className="
              absolute
              left-0
              top-full

              z-9999
              mt-2

              w-full

              overflow-hidden

              rounded-xl
              border
              border-stroke

              bg-white
              shadow-4
              dark:border-strokedark

              dark:bg-boxdark
            "
            >
              {/* Search */}
              <div
                className="
                border-b
                border-stroke
                p-2
              "
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar cliente..."
                  className="
                  h-[40px]
                  w-full

                  rounded-lg
                  border
                  border-stroke

                  bg-gray

                  px-3

                  text-sm
                  text-black

                  outline-none

                  transition-all

                  focus:border-primary

                  dark:border-strokedark
                  dark:bg-boxdark-2
                  dark:text-white
                "
                />
              </div>

              {/* Options */}
              <ul
                className="
                max-h-72
                overflow-y-auto
              "
              >
                {filteredOptions.map((option) => (
                  <li
                    key={option.id}
                    onClick={() => {
                      onSelect(option);

                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className={`
                    cursor-pointer

                    px-4
                    py-3

                    text-sm

                    transition-all

                    ${
                      option.id === selectedValue
                        ? `
                          bg-primary/10
                          font-semibold
                          text-primary
                        `
                        : `
                          text-black
                          hover:bg-gray

                          dark:text-bodydark1
                          dark:hover:bg-meta-4
                        `
                    }
                  `}
                  >
                    {option.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ================================================= */}
        {/* 🔹 Reload */}
        {/* ================================================= */}

        <button
          type="button"
          onClick={onReload}
          className="
          flex
          h-[44px]
          w-[44px]

          items-center
          justify-center

          rounded-xl

          border
          border-stroke

          bg-white
          text-body
          shadow-1

          transition-all

          duration-200

          hover:border-success
          hover:text-success

          hover:shadow-2
          dark:border-strokedark
          dark:bg-boxdark-2
        "
        >
          <SvgIcon name="refresh" />
        </button>

        {/* ================================================= */}
        {/* 🔹 Add */}
        {/* ================================================= */}

        <button
          type="button"
          onClick={openCustomerModal}
          className="
          flex
          h-[43px]
          w-[43px]

          items-center
          justify-center

          rounded-xl

          bg-primary

          text-white

          shadow-1

          transition-all
          duration-200

          hover:opacity-95
          hover:shadow-2
        "
        >
          <SvgIcon name="plus" className="h-5 w-5" />
        </button>
      </div>

      {/* 🔹 Error */}
      {error && (
        <p
          className="
          mt-1
          pl-1

          text-xs
          font-medium

          text-danger
        "
        >
          {error}
        </p>
      )}

      {/* ================================================= */}
      {/* 🔹 Modal */}
      {/* ================================================= */}

      <CustomerRegistrationPopup
        isOpen={showCustomerModal}
        onClose={closeCustomerModal}
        onSubmit={onCustomerCreated}
      />
    </div>
  );
};

export default POSCustomerField;
